import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { http, formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function InviteClaim() {
  const { token } = useParams();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState("loading"); // loading | ready | invalid | submitting
  const [invite, setInvite] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ password: "", confirm: "", name: "" });

  useEffect(() => {
    http.get(`/invites/${token}`)
      .then(({ data }) => { setInvite(data); setForm((f) => ({ ...f, name: data.student_name || "" })); setState("ready"); })
      .catch((e) => { setErr(formatError(e.response?.data?.detail) || "Invite invalid"); setState("invalid"); });
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (form.password.length < 6) { setErr("Password must be at least 6 characters."); return; }
    if (form.password !== form.confirm) { setErr("Passwords don't match."); return; }
    setState("submitting");
    try {
      const { data } = await http.post(`/invites/${token}/claim`, { password: form.password, name: form.name });
      localStorage.setItem("cc_token", data.token);
      await refresh();
      toast.success("Account claimed! Welcome to QuadScrub.");
      navigate("/student", { replace: true });
    } catch (e) {
      setErr(formatError(e.response?.data?.detail) || "Could not claim invite");
      setState("ready");
    }
  }

  return (
    <div className="min-h-[calc(100vh-80px)] flex items-center justify-center px-6 py-12 grid-bg">
      <Card className="w-full max-w-md border-border/60">
        <CardContent className="p-8">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center text-primary-foreground"><Sparkles size={16} /></div>
            <div className="font-display font-bold text-primary">QuadScrub</div>
          </div>

          {state === "loading" && (
            <div className="text-center py-8"><Loader2 className="animate-spin mx-auto text-secondary" /><p className="mt-3 text-sm text-muted-foreground">Checking your invite…</p></div>
          )}

          {state === "invalid" && (
            <div className="text-center py-6">
              <XCircle className="mx-auto text-destructive" size={48} />
              <h1 className="font-display text-2xl font-bold text-primary mt-3">Invite unavailable</h1>
              <p className="text-sm text-muted-foreground mt-2">{err}</p>
              <p className="text-xs text-muted-foreground mt-4">Ask your parent to send a new invite, or sign in if you already have an account.</p>
              <Button className="mt-4" variant="outline" onClick={() => navigate("/login")} data-testid="invite-go-login">Go to sign in</Button>
            </div>
          )}

          {(state === "ready" || state === "submitting") && invite && (
            <>
              <CheckCircle2 className="text-secondary mb-3" />
              <h1 className="font-display text-3xl font-bold text-primary">Welcome, {invite.student_name?.split(" ")[0] || "friend"}!</h1>
              <p className="text-sm text-muted-foreground mt-1">
                {invite.parent_name} set up QuadScrub for you. Create a password to claim your account.
              </p>
              <div className="mt-4 p-3 rounded-lg bg-muted/40 text-xs text-muted-foreground">
                Signing in as <b className="text-primary">{invite.email}</b>
              </div>

              <form className="mt-6 space-y-4" onSubmit={submit}>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Your name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="invite-name" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Create password</Label>
                  <Input type="password" minLength={6} required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="invite-password" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Confirm password</Label>
                  <Input type="password" minLength={6} required value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} data-testid="invite-confirm" />
                </div>
                {err && <div className="text-sm text-destructive">{err}</div>}
                <Button type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={state === "submitting"} data-testid="invite-claim-submit">
                  {state === "submitting" ? "Claiming…" : "Claim my account"}
                </Button>
                <p className="text-[11px] text-muted-foreground">Billing stays with your parent — you'll see your cleanings, leave notes for the cleaner, and share access codes.</p>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
